import { createApp } from 'vue';
import './style.css';
import App from './App.vue';
import { createPinia } from 'pinia';
import { useDonorStore } from '@/stores/DonorStore';

import router from './router';
import { Skeletor } from 'vue-skeletor';
import PrimeVue from 'primevue/config';
import Material from '@primevue/themes/material';
import { definePreset } from '@primevue/themes';

import AutoComplete from 'primevue/autocomplete';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Dialog from 'primevue/dialog';
import FileUpload from 'primevue/fileupload';
import FloatLabel from 'primevue/floatlabel';
import FocusTrap from 'primevue/focustrap';
import { Form, FormField } from '@primevue/forms';
import Image from 'primevue/image';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import KeyFilter from 'primevue/keyfilter';
import Menu from 'primevue/menu';
import Message from 'primevue/message';
import Panel from 'primevue/panel';
import Password from 'primevue/password';
import ProgressBar from 'primevue/progressbar';
import RadioButton from 'primevue/radiobutton';
import RadioButtonGroup from 'primevue/radiobuttongroup';
import Skeleton from 'primevue/skeleton';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Select from 'primevue/select';
import Drawer from 'primevue/drawer';
import Fieldset from 'primevue/fieldset';
import Paginator from 'primevue/paginator';
import Divider from 'primevue/divider';
import Chip from 'primevue/chip';
import Popover from 'primevue/popover';
import Dropdown from 'primevue/dropdown';

import LabeledField from '@/components/forms/LabeledField.vue';
import ValidationMessage from '@/components/forms/ValidationMessage.vue';

// TODO: https://primevue.org/theming/styled/#utils Should Implement this to have the site colors update based on Host Color scheme
const DonorPreset = definePreset(Material, {
  components: {
    button: {
      sm: {
        font: {
          size: '14px',
        },
        padding: {
          y: '4px',
        },
      },
      lg: {
        font: {
          size: '18px',
        },
      },
      primaryHoverColor: 'red',
    },
    paginator: {
      background: 'transparent',
      navButtonSelectedBackground: '{primary.500}',
      navButtonHoverBackground: '{primary.100}',
    },
  },
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          inverseColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.800}',
        },
        highlight: {
          background: '{blue.950}',
          focusBackground: '{blue.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{primary.50}',
          inverseColor: '{primary.950}',
          hoverColor: '{primary.100}',
          activeColor: '{primary.200}',
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
});

const app = createApp(App);
app.use(createPinia());
app.use(router);

app.use(PrimeVue, {
  ripple: true,

  theme: {
    preset: DonorPreset,
    options: {
      darkModeSelector: false || 'none',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
});

app.use(ToastService);

// Default to validate forms on mount to populate initial state. Works in conjunction with `pristine` class to avoid
// showing error styles on initial load.
Form.extends.props.validateOnMount.default = true;

app.directive('focustrap', FocusTrap);
app.directive('keyfilter', KeyFilter);

app.component('AutoComplete', AutoComplete);
app.component('Badge', Badge);
app.component('Button', Button);
app.component('Card', Card);
app.component('Dialog', Dialog);
app.component('FileUpload', FileUpload);
app.component('FloatLabel', FloatLabel);
app.component('Form', Form);
app.component('FormField', FormField);
app.component('Image', Image);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('InputNumber', InputNumber);
app.component('InputText', InputText);
app.component('Menu', Menu);
app.component('Message', Message);
app.component('Panel', Panel);
app.component('Password', Password);
app.component('ProgressBar', ProgressBar);
app.component('RadioButton', RadioButton);
app.component('RadioButtonGroup', RadioButtonGroup);
app.component('Skeleton', Skeleton);
app.component('Tag', Tag);
app.component('Textarea', Textarea);
app.component('Toast', Toast);
app.component('Select', Select);
app.component('Drawer', Drawer);
app.component('Fieldset', Fieldset);
app.component('Paginator', Paginator);
app.component('Divider', Divider);
app.component('Chip', Chip);
app.component('Popover', Popover);
app.component('Dropdown', Dropdown);

app.component('LabeledField', LabeledField);
app.component('ValidationMessage', ValidationMessage);

app.component(Skeletor.name, Skeletor);

app.mount('#app');
const donorStore = useDonorStore();
window.donorStore = donorStore;